import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  TemplateBodyAutocomplete,
  substituteTemplateVariables,
  convertNamedVariablesToNumbered,
  convertNumberedVariablesToNamed,
} from '../../ui/template-body-autocomplete';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { HsmTemplate } from '../../../types';
import { getTemplateStatusBadge } from '../templateHelpers';

export interface EditTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: HsmTemplate | null;
  updating: boolean;
  onSubmit: (
    templateId: string,
    data: {
      bodyText: string;
      headerText?: string;
      headerType?: 'TEXT';
      footerText?: string;
      bodyExamples?: string[];
      headerExample?: string;
    }
  ) => Promise<{ success: boolean; error?: string }>;
  onClose: () => void;
  onSaveVariableMapping: (
    templateName: string,
    variableMapping: string[]
  ) => Promise<{ success: boolean; error?: string }>;
  onGetVariableMapping: (
    templateName: string
  ) => Promise<{ success: boolean; variableMapping?: string[] | null; error?: string }>;
}

export function EditTemplateDialog({
  open,
  onOpenChange,
  template,
  updating,
  onSubmit,
  onClose,
  onSaveVariableMapping,
  onGetVariableMapping,
}: EditTemplateDialogProps) {
  // Form state
  const [editTemplateBodyText, setEditTemplateBodyText] = useState('');
  const [editTemplateHeaderEnabled, setEditTemplateHeaderEnabled] = useState(false);
  const [editTemplateHeaderText, setEditTemplateHeaderText] = useState('');
  const [editTemplateFooterEnabled, setEditTemplateFooterEnabled] = useState(false);
  const [editTemplateFooterText, setEditTemplateFooterText] = useState('');
  const [editTemplateHeaderExample, setEditTemplateHeaderExample] = useState('');
  const [editTemplateError, setEditTemplateError] = useState<string | null>(null);
  const [, setEditVariableMapping] = useState<string[]>([]);

  // Extract header placeholder
  const editHeaderHasPlaceholder = useMemo(() => {
    return editTemplateHeaderEnabled && /\{\{1\}\}/.test(editTemplateHeaderText);
  }, [editTemplateHeaderEnabled, editTemplateHeaderText]);

  // Generate preview text
  const editPreviewBodyText = useMemo(() => {
    return substituteTemplateVariables(editTemplateBodyText);
  }, [editTemplateBodyText]);

  const editPreviewHeaderText = useMemo(() => {
    if (!editTemplateHeaderEnabled || !editTemplateHeaderText) return '';
    if (editHeaderHasPlaceholder) {
      return editTemplateHeaderText.replace(
        /\{\{1\}\}/,
        editTemplateHeaderExample || '[Exemplo Cabecalho]'
      );
    }
    return editTemplateHeaderText;
  }, [
    editTemplateHeaderEnabled,
    editTemplateHeaderText,
    editHeaderHasPlaceholder,
    editTemplateHeaderExample,
  ]);

  // Load template data when dialog opens
  useEffect(() => {
    if (open && template) {
      const loadTemplateData = async () => {
        const bodyComponent = template.components?.find((c) => c.type === 'BODY');
        const rawBodyText = bodyComponent?.text || '';

        // Try to fetch variable mapping to convert {{1}} back to {{Nome}}
        try {
          const result = await onGetVariableMapping(template.name);
          if (result.success && result.variableMapping && result.variableMapping.length > 0) {
            setEditVariableMapping(result.variableMapping);
            const convertedText = convertNumberedVariablesToNamed(
              rawBodyText,
              result.variableMapping
            );
            setEditTemplateBodyText(convertedText);
          } else {
            setEditVariableMapping([]);
            setEditTemplateBodyText(rawBodyText);
          }
        } catch {
          setEditVariableMapping([]);
          setEditTemplateBodyText(rawBodyText);
        }

        const headerComponent = template.components?.find((c) => c.type === 'HEADER');
        if (headerComponent?.text) {
          setEditTemplateHeaderEnabled(true);
          setEditTemplateHeaderText(headerComponent.text);
          if (headerComponent.example?.header_text?.[0]) {
            setEditTemplateHeaderExample(headerComponent.example.header_text[0]);
          }
        } else {
          setEditTemplateHeaderEnabled(false);
          setEditTemplateHeaderText('');
          setEditTemplateHeaderExample('');
        }

        const footerComponent = template.components?.find((c) => c.type === 'FOOTER');
        if (footerComponent?.text) {
          setEditTemplateFooterEnabled(true);
          setEditTemplateFooterText(footerComponent.text);
        } else {
          setEditTemplateFooterEnabled(false);
          setEditTemplateFooterText('');
        }

        setEditTemplateError(null);
      };

      loadTemplateData();
    }
  }, [open, template, onGetVariableMapping]);

  // Handle edit template submission
  const handleSubmit = useCallback(async () => {
    if (!template) return;

    if (!editTemplateBodyText.trim()) {
      setEditTemplateError('Corpo da mensagem e obrigatorio');
      return;
    }

    setEditTemplateError(null);

    // Convert named variables to numbered placeholders
    const { convertedText, bodyExamples, variableNames } =
      convertNamedVariablesToNumbered(editTemplateBodyText.trim());

    const data = {
      bodyText: convertedText,
      ...(editTemplateHeaderEnabled &&
        editTemplateHeaderText.trim() && {
          headerText: editTemplateHeaderText.trim(),
          headerType: 'TEXT' as const,
        }),
      ...(editTemplateFooterEnabled &&
        editTemplateFooterText.trim() && {
          footerText: editTemplateFooterText.trim(),
        }),
      ...(bodyExamples.length > 0 && {
        bodyExamples,
      }),
      ...(editHeaderHasPlaceholder &&
        editTemplateHeaderExample.trim() && {
          headerExample: editTemplateHeaderExample.trim(),
        }),
    };

    const result = await onSubmit(template.id, data);

    if (result.success) {
      // Auto-save the variable mapping if there are any variables
      if (variableNames.length > 0) {
        onSaveVariableMapping(template.name, variableNames).catch((err) => {
          console.warn('Failed to save template variable mapping:', err);
        });
      }

      onClose();
    } else {
      setEditTemplateError(result.error || 'Erro ao atualizar template');
    }
  }, [
    template,
    editTemplateBodyText,
    editTemplateHeaderEnabled,
    editTemplateHeaderText,
    editTemplateFooterEnabled,
    editTemplateFooterText,
    editHeaderHasPlaceholder,
    editTemplateHeaderExample,
    onSubmit,
    onClose,
    onSaveVariableMapping,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Template</DialogTitle>
          <DialogDescription>
            Edite o conteudo do template. O template sera reenviado para aprovacao do Meta.
            <br />
            <span className="text-yellow-600 dark:text-yellow-400">
              Nota: Nome, categoria e idioma nao podem ser alterados apos a criacao.
            </span>
          </DialogDescription>
        </DialogHeader>

        {template && (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Left Side - Form */}
            <div className="space-y-4">
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  Informacoes do Template (nao editaveis)
                </Label>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Nome</div>
                    <div className="font-medium">{template.name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Categoria</div>
                    <div className="font-medium">{template.category}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Idioma</div>
                    <div className="font-medium">{template.language}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-muted-foreground">Status atual:</span>
                  {getTemplateStatusBadge(template.status)}
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="edit-template-header-toggle">Cabecalho</Label>
                  <p className="text-xs text-muted-foreground">
                    Adicionar texto de cabecalho (opcional)
                  </p>
                </div>
                <Switch
                  id="edit-template-header-toggle"
                  checked={editTemplateHeaderEnabled}
                  onCheckedChange={setEditTemplateHeaderEnabled}
                />
              </div>

              {editTemplateHeaderEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="edit-template-header">Texto do Cabecalho</Label>
                  <Input
                    id="edit-template-header"
                    value={editTemplateHeaderText}
                    onChange={(e) => setEditTemplateHeaderText(e.target.value)}
                    placeholder="Titulo ou cabecalho da mensagem"
                  />
                </div>
              )}

              {editHeaderHasPlaceholder && (
                <div className="space-y-2">
                  <Label htmlFor="edit-template-header-example">Exemplo do Cabecalho</Label>
                  <Input
                    id="edit-template-header-example"
                    value={editTemplateHeaderExample}
                    onChange={(e) => setEditTemplateHeaderExample(e.target.value)}
                    placeholder="Ex: Joao Silva"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="edit-template-body">Corpo da Mensagem *</Label>
                <TemplateBodyAutocomplete
                  id="edit-template-body"
                  value={editTemplateBodyText}
                  onChange={setEditTemplateBodyText}
                  placeholder="Ola {{Nome}}, sua empresa {{Empresa}} esta confirmada!"
                  minHeight="120px"
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="edit-template-footer-toggle">Rodape</Label>
                  <p className="text-xs text-muted-foreground">
                    Adicionar texto de rodape (opcional)
                  </p>
                </div>
                <Switch
                  id="edit-template-footer-toggle"
                  checked={editTemplateFooterEnabled}
                  onCheckedChange={setEditTemplateFooterEnabled}
                />
              </div>

              {editTemplateFooterEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="edit-template-footer">Texto do Rodape</Label>
                  <Input
                    id="edit-template-footer"
                    value={editTemplateFooterText}
                    onChange={(e) => setEditTemplateFooterText(e.target.value)}
                    placeholder="Ex: Responda SAIR para cancelar"
                  />
                </div>
              )}
            </div>

            {/* Right Side - Preview */}
            <div className="space-y-4">
              <Label className="text-sm font-medium">Preview da Mensagem</Label>
              <div className="rounded-lg border bg-gray-100 dark:bg-gray-900 p-4 min-h-[300px]">
                <div className="max-w-[280px] mx-auto">
                  <div className="rounded-lg bg-green-100 dark:bg-green-900/50 p-3 shadow-sm relative">
                    <div className="absolute -left-2 top-0 w-0 h-0 border-t-[10px] border-t-green-100 dark:border-t-green-900/50 border-l-[10px] border-l-transparent" />

                    {editTemplateHeaderEnabled && editPreviewHeaderText && (
                      <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2 pb-2 border-b border-green-200 dark:border-green-800">
                        {editPreviewHeaderText}
                      </div>
                    )}

                    <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                      {editPreviewBodyText || (
                        <span className="text-gray-400 dark:text-gray-500 italic">
                          O preview aparecera aqui...
                        </span>
                      )}
                    </div>

                    {editTemplateFooterEnabled && editTemplateFooterText && (
                      <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-800 text-xs text-gray-500 dark:text-gray-400">
                        {editTemplateFooterText}
                      </div>
                    )}

                    <div className="text-right mt-1">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400">
                        {new Date().toLocaleTimeString('pt-BR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800 p-3">
                <p className="text-xs text-yellow-700 dark:text-yellow-400">
                  Ao salvar alteracoes, o template sera reenviado para aprovacao do Meta. O status
                  mudara para "Pendente" ate ser aprovado novamente.
                </p>
              </div>
            </div>
          </div>
        )}

        {editTemplateError && (
          <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3">
            <p className="text-sm text-red-600 dark:text-red-400">{editTemplateError}</p>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={updating}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={updating || !editTemplateBodyText.trim()}
          >
            {updating ? 'Salvando...' : 'Salvar e Reenviar para Aprovacao'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
